import React, { useState } from 'react';
import {
    Box, Paper, Typography, Button, TextField,
    Grid, Divider, IconButton, MenuItem, Alert, Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Save as SaveIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { getGrades, SchoolType } from '../utils/gradeUtils';

const CreateFeeStructurePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // URL Context
    const urlGrade = searchParams.get('grade') || '';
    const urlTerm = searchParams.get('term') || 'Term 1';
    const urlYear = searchParams.get('academicYear') || new Date().getFullYear().toString();
    const returnTo = searchParams.get('returnTo');
    const studentId = searchParams.get('studentId');
    const action = searchParams.get('action');

    const [formData, setFormData] = useState({
        name: urlGrade ? `${urlGrade} ${urlTerm} ${urlYear} Fees` : '',
        grade: urlGrade,
        term: urlTerm,
        academicYear: urlYear,
    });
    const [items, setItems] = useState([{ description: 'Tuition Fee', amount: '', isOptional: false }]);
    const [schoolType, setSchoolType] = useState<SchoolType>('PRIMARY');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [preloading, setPreloading] = useState(false);

    React.useEffect(() => {
        const fetchSchoolType = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(getApiUrl('school'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSchoolType(data.schoolType || 'PRIMARY');
            } catch (error) {
                console.error('Error fetching school type:', error);
            }
        };
        fetchSchoolType();
    }, []);

    const handleItemChange = (index: number, field: string, value: any) => {
        const newItems: any = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { description: '', amount: '', isOptional: false }]);
    };

    const removeItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    };

    const handleSubmit = async () => {
        setError('');
        setLoading(true);
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            await axios.post(getApiUrl('fees/structures'), {
                ...formData,
                items
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (returnTo) {
                const backUrl = `${returnTo}?studentId=${studentId}&action=${action}`;
                navigate(backUrl);
            } else {
                navigate('/dashboard/fees');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create fee structure');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>Back</Button>
                <Typography variant="h5" fontWeight={700} color="#1a237e">New Fee Structure</Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: '1px solid #f0f0f0', maxWidth: 800, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Grid container spacing={3} mb={4}>
                    <Grid size={12}>
                        <TextField
                            label="Structure Name"
                            fullWidth
                            placeholder="e.g., Grade 5 Term 1 Fees"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            label="Grade"
                            select
                            fullWidth
                            value={formData.grade}
                            onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                        >
                            {getGrades(schoolType).map(g => (
                                <MenuItem key={g} value={g}>{g}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            label="Term"
                            select
                            fullWidth
                            value={formData.term}
                            onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                        >
                            {['Term 1', 'Term 2', 'Term 3'].map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            label="Year"
                            type="number"
                            fullWidth
                            value={formData.academicYear}
                            onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>Fee Items</Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="#1a237e">Total: ${calculateTotal().toLocaleString()}</Typography>
                </Box>

                {items.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <TextField
                            label="Description"
                            fullWidth
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        />
                        <TextField
                            label="Amount"
                            type="number"
                            sx={{ width: 200 }}
                            value={item.amount}
                            onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                        />
                        <IconButton onClick={() => removeItem(index)} color="error" disabled={items.length === 1}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ))}

                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <Button startIcon={<AddIcon />} onClick={addItem} variant="outlined">
                        Add Line Item
                    </Button>
                    <Button
                        startIcon={<AutorenewIcon />}
                        onClick={async () => {
                            setPreloading(true);
                            try {
                                const userInfo = localStorage.getItem('userInfo');
                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                const { data } = await axios.get(getApiUrl('fees/defaults'), {
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                setItems(data);
                            } catch (err) {
                                console.error('Failed to preload defaults:', err);
                            } finally {
                                setPreloading(false);
                            }
                        }}
                        disabled={preloading}
                        color="secondary"
                        variant="outlined"
                    >
                        {preloading ? 'Loading...' : 'Preload Standard Services'}
                    </Button>
                </Stack>

                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ bgcolor: '#1a237e', py: 1.5 }}
                >
                    {loading ? 'Saving...' : 'Save Fee Structure'}
                </Button>
            </Paper>
        </Box>
    );
};

export default CreateFeeStructurePage;
